import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CurrencyCode, CURRENCIES } from '../config/currency';
import { roundMoney } from '../utils/currency';

interface ExchangeRate {
  currencyCode: CurrencyCode;
  rate: number;
}

/**
 * Interface pour les métadonnées de conversion
 */
interface ConversionResult {
  amount: number;
  rate: number;
  fromCurrency: CurrencyCode;
  toCurrency: CurrencyCode;
}

interface CurrencyState {
  // Devise de base (généralement celle de l'entreprise - CDF par défaut)
  baseCurrency: CurrencyCode;
  // Devise active pour l'affichage
  displayCurrency: CurrencyCode;
  // Taux de change par rapport à la devise de base (1 [currency] = [rate] CDF)
  exchangeRates: Record<CurrencyCode, number>;
  // Dernière mise à jour des taux
  lastRatesUpdate: string | null;
  // Indicateur de chargement
  isLoading: boolean;
  
  // Actions
  setBaseCurrency: (currency: CurrencyCode) => void;
  setDisplayCurrency: (currency: CurrencyCode) => void;
  setExchangeRate: (currencyCode: CurrencyCode, rate: number) => void;
  updateExchangeRates: (rates: ExchangeRate[]) => void;
  setRatesFromRecord: (rates: Record<string, number>) => void;
  setLoading: (loading: boolean) => void;
  
  // Conversion entre devises
  convert: (amount: number, fromCurrency: CurrencyCode, toCurrency: CurrencyCode) => number;
  convertWithDetails: (amount: number, fromCurrency: CurrencyCode, toCurrency: CurrencyCode) => ConversionResult;
  
  // Calcul du montant en devise de base
  toBaseCurrency: (amount: number, currency: CurrencyCode) => number;
  fromBaseCurrency: (amount: number, targetCurrency: CurrencyCode) => number;
  
  // Obtenir le taux actuel
  getRate: (currency: CurrencyCode) => number;
  hasValidRate: (currency: CurrencyCode) => boolean;
}

// Initialiser avec des taux de change par défaut (1 pour la devise de base)
const defaultExchangeRates = Object.keys(CURRENCIES).reduce((rates, code) => {
  rates[code as CurrencyCode] = code === 'CDF' ? 1 : 0;
  return rates;
}, {} as Record<CurrencyCode, number>);

export const useCurrencyStore = create<CurrencyState>()(
  persist(
    (set, get) => ({
      baseCurrency: 'CDF',
      displayCurrency: 'CDF',
      exchangeRates: defaultExchangeRates,
      lastRatesUpdate: null,
      isLoading: false,
      
      setBaseCurrency: (currency) => set({ baseCurrency: currency }),
      
      setDisplayCurrency: (currency) => set({ displayCurrency: currency }),
      
      setExchangeRate: (currencyCode, rate) => set((state) => ({
        exchangeRates: {
          ...state.exchangeRates,
          [currencyCode]: rate
        },
        lastRatesUpdate: new Date().toISOString()
      })),
      
      updateExchangeRates: (rates) => set((state) => {
        const newRates = { ...state.exchangeRates };
        rates.forEach(({ currencyCode, rate }) => {
          newRates[currencyCode] = rate;
        });
        return { 
          exchangeRates: newRates,
          lastRatesUpdate: new Date().toISOString()
        };
      }),
      
      setRatesFromRecord: (rates) => set((state) => {
        const newRates = { ...state.exchangeRates };
        Object.entries(rates).forEach(([code, rate]) => {
          if (code in CURRENCIES) {
            newRates[code as CurrencyCode] = rate;
          }
        });
        return {
          exchangeRates: newRates,
          lastRatesUpdate: new Date().toISOString()
        };
      }),
      
      setLoading: (loading) => set({ isLoading: loading }),
      
      getRate: (currency) => {
        const state = get();
        return state.exchangeRates[currency] || 0;
      },
      
      hasValidRate: (currency) => {
        const state = get();
        const rate = state.exchangeRates[currency];
        return currency === state.baseCurrency || (typeof rate === 'number' && rate > 0);
      },
      
      toBaseCurrency: (amount, currency) => {
        const state = get();
        if (currency === state.baseCurrency) return amount;
        
        const rate = state.exchangeRates[currency];
        if (!rate || rate <= 0) {
          console.warn(`Taux de change invalide pour ${currency}`);
          return amount;
        }
        // 1 [currency] = [rate] CDF, donc amount * rate = montant en CDF
        return roundMoney(amount * rate);
      },
      
      fromBaseCurrency: (amount, targetCurrency) => {
        const state = get();
        if (targetCurrency === state.baseCurrency) return amount;
        
        const rate = state.exchangeRates[targetCurrency];
        if (!rate || rate <= 0) {
          console.warn(`Taux de change invalide pour ${targetCurrency}`);
          return amount;
        }
        // amount en CDF / rate = montant en devise cible
        return roundMoney(amount / rate);
      },
      
      convert: (amount, fromCurrency, toCurrency) => {
        const state = get();
        // Si les devises sont identiques, pas de conversion nécessaire
        if (fromCurrency === toCurrency) return amount;
        
        // Récupérer les taux de change
        const fromRate = state.exchangeRates[fromCurrency];
        const toRate = state.exchangeRates[toCurrency];
        
        // Vérifier que les taux sont valides
        if (fromRate <= 0 || toRate <= 0) {
          console.error(`Taux de change invalides: ${fromCurrency}=${fromRate}, ${toCurrency}=${toRate}`);
          return amount;
        }
        
        // Convertir via la devise de base (CDF)
        if (fromCurrency === state.baseCurrency) {
          // De CDF vers devise cible: CDF / rate
          return roundMoney(amount / toRate);
        } else if (toCurrency === state.baseCurrency) {
          // De devise vers CDF: amount * rate
          return roundMoney(amount * fromRate);
        } else {
          // Entre deux devises étrangères via CDF
          const amountInBase = amount * fromRate;
          return roundMoney(amountInBase / toRate);
        }
      },
      
      convertWithDetails: (amount, fromCurrency, toCurrency) => {
        const state = get();
        const convertedAmount = state.convert(amount, fromCurrency, toCurrency);
        const fromRate = state.exchangeRates[fromCurrency];
        const toRate = state.exchangeRates[toCurrency];
        
        // Calculer le taux effectif
        let effectiveRate = 1;
        if (fromCurrency === state.baseCurrency) {
          effectiveRate = 1 / toRate;
        } else if (toCurrency === state.baseCurrency) {
          effectiveRate = fromRate;
        } else {
          effectiveRate = fromRate / toRate;
        }
        
        return {
          amount: convertedAmount,
          rate: effectiveRate,
          fromCurrency,
          toCurrency
        };
      }
    }),
    {
      name: 'currency-storage',
      partialize: (state) => ({
        baseCurrency: state.baseCurrency,
        displayCurrency: state.displayCurrency,
        exchangeRates: state.exchangeRates,
        lastRatesUpdate: state.lastRatesUpdate
      })
    }
  )
);
